import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ButtonAlertMiniMap.css';
import { DEFAULT_MAP_INITIAL_VIEW, getMapInitialView } from '../utils/mapInitialView';

export type ButtonAlertMapMarker = {
  id: number;
  lat: number;
  lng: number;
  label?: string;
  selected?: boolean;
};

const DEFAULT_CENTER: L.LatLngExpression = [DEFAULT_MAP_INITIAL_VIEW.lat, DEFAULT_MAP_INITIAL_VIEW.lng];
const DEFAULT_ZOOM = 13;

function markerIcon(selected: boolean): L.DivIcon {
  const color = selected ? '#A61B34' : '#dc2626';
  return L.divIcon({
    className: 'button-alert-mini-map__marker',
    html: `<div class="button-alert-mini-map__dot" style="background:${color}"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9]
  });
}

interface ButtonAlertMiniMapProps {
  markers: ButtonAlertMapMarker[];
  focusId?: number | null;
}

const ButtonAlertMiniMap: React.FC<ButtonAlertMiniMapProps> = ({ markers, focusId }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const map = L.map(el, {
      zoomControl: true,
      attributionControl: true
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    const group = L.layerGroup().addTo(map);
    mapRef.current = map;
    layerRef.current = group;

    // Ajustar centro con la config del backend (MAP_INITIAL_*).
    // Si aún no hay markers, aplicar; si ya llegaron, fitBounds ya tomó control.
    getMapInitialView().then((view) => {
      if (!mapRef.current) return;
      if (group.getLayers().length === 0) {
        mapRef.current.setView([view.lat, view.lng], DEFAULT_ZOOM);
      }
    }).catch(() => { /* fallback ya aplicado */ });

    const ro = new ResizeObserver(() => {
      map.invalidateSize();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const group = layerRef.current;
    if (!map || !group) return;

    group.clearLayers();

    for (const m of markers) {
      if (!Number.isFinite(m.lat) || !Number.isFinite(m.lng)) continue;
      const isSel = focusId != null ? m.id === focusId : !!m.selected;
      const mk = L.marker([m.lat, m.lng], { icon: markerIcon(isSel) });
      if (m.label) mk.bindTooltip(m.label, { direction: 'top' });
      mk.addTo(group);
    }

    if (markers.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    const focused = focusId != null ? markers.find((x) => x.id === focusId) : null;
    if (focused && Number.isFinite(focused.lat) && Number.isFinite(focused.lng)) {
      map.setView([focused.lat, focused.lng], 16);
      return;
    }

    const bounds = L.latLngBounds(
      markers.filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng)).map((m) => [m.lat, m.lng] as L.LatLngTuple)
    );
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
    }
  }, [markers, focusId]);

  return <div ref={containerRef} className="button-alert-mini-map" role="presentation" />;
};

export default ButtonAlertMiniMap;
